import React, { useState } from "react";
import { Globe, Truck, ArrowLeftRight } from "lucide-react";
import globalSourcing from '../assets/images/GlobalSourcing.jpg';
import importExport from '../assets/images/exportimportservice.jpeg';
import quality from '../assets/images/qualityassurance.jpg';

const ServiceDetail = () => {
  const [activeService, setActiveService] = useState("oneForAll");

   const servicesData = {
    oneForAll: {
      title: "Global Sourcing",
      text: "Partnering with trusted suppliers to ensure premium quality and authenticity.",
      icon: <Globe />,
      gradient: 'linear-gradient(135deg, #3b82f6, #06b6d4)', // 🔵 Blue to Cyan
      image: globalSourcing,
    },
    import: {
      title: "Export & Import Management",
      text: "Complete handling of documentation, logistics, and international compliance.",
      icon: <Truck />,
      gradient: 'linear-gradient(135deg, #3b82f6, #06b6d4)', // same palette
      image: importExport,
    },
    export: {
      title: "Quality Assurance",
      text: "Rigorous testing and certification for every batch.",
      icon: <ArrowLeftRight />,
      gradient: 'linear-gradient(135deg, #3b82f6, #06b6d4)', // same palette
      image: quality,
    },
  };

  const decorativeDotStyle = (top, left, gradient, size = '12px') => ({
    position: 'absolute',
    width: size,
    height: size,
    borderRadius: '50%',
    background: gradient,
    top,
    left,
    opacity: 0.7,
    boxShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
  });

  const serviceCardStyle = (isActive, gradient) => ({
    display: 'flex',
    gap: '1.5rem',
    padding: '2rem',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
    border: isActive ? `2px solid transparent` : '2px solid transparent',
    backgroundImage: isActive ? `${gradient}` : 'none',
    backgroundClip: isActive ? 'padding-box' : 'padding-box',
    position: 'relative',
    transform: isActive ? 'translateX(8px)' : 'translateX(0)',
    boxShadow: isActive ? '0 10px 30px rgba(0, 0, 0, 0.3)' : 'none',
  });

  const iconBoxStyle = (gradient) => ({
    width: '85px',
    height: '85px',
    minWidth: '85px',
    borderRadius: '18px',
    background: gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    color: '#ffffff',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  });

  // ✅ Updated: Removed overlay + dark blend from images
  const mediaBoxStyle = (gradient, image) => ({
    borderRadius: '24px',
    height: '550px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: `url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
  });

  const badgeStyle = (gradient) => ({
    right: '20px',
    background: gradient,
    padding: '15px 26px',
    borderRadius: '12px',
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '0.9rem',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    width: '23%',
  });

  return (
    <>
      <style>{`
        .service-container {
          background-color:#d6d6c2;
          min-height: 100vh;
          padding: 4rem 2rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .service-main-card {
          max-width: 1400px;
          margin: 0 auto;
          background-color: #0d6efd;
          border-radius: 32px;
          padding: 3.5rem;
          border: 2px solid #ffffff;
          position: relative;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .service-title {
          color: #ffffff;
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 3rem;
          letter-spacing: -0.5px;
        }

        .service-content-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: start;
        }

        .services-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .service-media-box img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 24px;
        }

        .service-badge {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 28px;
          border-radius: 25px;
          color: #ffffff;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
        }

        .service-badge:hover {
          transform: translateX(-50%) scale(1.05);
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        /* Responsive tweaks (same as before) */
        @media (max-width: 991px) {
          .service-content-wrapper {
            grid-template-columns: 1fr;
          }
          .service-media-box {
            height: 400px !important;
          }
        }
      `}</style>

      <div className="service-container">
        <div style={{ textAlign: 'center' }}>
          <h2
            className="section-title mb-3 pt-5"
            style={{
              display: 'inline-block',
              position: 'relative',
              paddingBottom: '10px',
            }}
          >
            Services
            <span
              style={{
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '4px',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, #5294ea, #42b883)',
              }}
            ></span>
          </h2>
        </div>

        <div className="service-main-card">
          {/* Decorative Dots */}
          <div className="decorative-dot" style={decorativeDotStyle('8%', '95%', 'linear-gradient(135deg, #ec4899, #9333ea)', '18px')}></div>
          <div className="decorative-dot" style={decorativeDotStyle('25%', '4%', 'linear-gradient(135deg, #4ade80, #0d9488)', '14px')}></div>

          <h2 className="service-title">How it Works:</h2>

          <div className="service-content-wrapper">
            {/* Left Side - List */}
            <div className="services-list">
              {Object.keys(servicesData).map((key) => {
                const service = servicesData[key];
                const isActive = activeService === key;
                return (
                  <div
                    key={key}
                    className="service-card-item"
                    style={serviceCardStyle(isActive, service.gradient)}
                    onClick={() => setActiveService(key)}
                  >
                    <div className="service-icon-box" style={iconBoxStyle(service.gradient)}>
                      {service.icon}
                    </div>
                    <div className="service-content">
                      <h3 className="service-item-title">{service.title}</h3>
                      <p className="service-item-text">{service.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Side - Image */}
            <div
              className="service-media-box"
              style={mediaBoxStyle(
                servicesData[activeService].gradient,
                servicesData[activeService].image
              )}
            >
              <a href={servicesData[activeService].link || '/services'} style={{ textDecoration: 'none' }}>
                <div
                  className="service-badge"
                  style={{
                    ...badgeStyle(servicesData[activeService].gradient),
                    cursor: 'pointer',
                  }}
                >
                  Read More
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;
